'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '@/app/context/AppContext';
import styles from './LanguageSwitcher.module.scss';

interface LangOption {
  locale: string;
  name: string;
  flagCode: string;
  label: string;
}

const FALLBACK: LangOption[] = [
  { locale: 'pt', name: 'Português', flagCode: 'pt', label: 'PT' },
  { locale: 'en', name: 'English', flagCode: 'gb', label: 'EN' },
];

const flagUrl = (code: string) =>
  `https://flagcdn.com/20x15/${code.toLowerCase()}.png`;

// Module-level cache - shared across renders
let cachedOptions: LangOption[] | null = null;

async function fetchLangs(): Promise<LangOption[]> {
  if (cachedOptions) return cachedOptions;
  try {
    const r = await fetch('/api/v1/languages', { cache: 'no-store' });
    if (!r.ok) throw new Error('fail');
    const j = await r.json() as { success: boolean; data?: LangOption[] };
    if (j.success && Array.isArray(j.data) && j.data.length > 0) {
      cachedOptions = j.data;
      return cachedOptions;
    }
  } catch { /* silent */ }
  cachedOptions = FALLBACK;
  return cachedOptions;
}

export default function LanguageSwitcher() {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<LangOption[]>(FALLBACK);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchLangs().then(setOptions).catch(() => { });
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Find current lang's flagCode from fetched options; fall back to known map or locale itself
  const current = options.find(o => o.locale === lang) ?? { locale: lang, name: lang.toUpperCase(), flagCode: lang === 'en' ? 'gb' : lang, label: lang.toUpperCase() };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        id="lang-switcher-btn"
        type="button"
        className={styles.trigger}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Idioma: ${current.name}`}
        onClick={() => setOpen(o => !o)}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={flagUrl(current.flagCode)}
          alt={current.name}
          className={styles.flag}
          width={18}
          height={13}
          onError={e => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
        />
        <span className={styles.code}>{lang.toUpperCase()}</span>
        <svg
          className={`${styles.chevron}${open ? ` ${styles.open}` : ''}`}
          width="10" height="10" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.5"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className={styles.dropdown} role="listbox" aria-label="Selecionar idioma">
          {options.map((opt) => (
            <button
              key={opt.locale}
              type="button"
              role="option"
              aria-selected={opt.locale === lang}
              data-active={opt.locale === lang}
              className={styles.option}
              onClick={() => { setLang(opt.locale); setOpen(false); }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={flagUrl(opt.flagCode)}
                alt={opt.name}
                className={styles.flag}
                width={18}
                height={13}
                onError={e => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
              />
              <span className={styles.optionName}>{opt.name}</span>
              {opt.locale === lang && (
                <svg className={styles.checkmark} width="14" height="14" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
